/**
 * Static ad zones and the interstitial takeover.
 *
 * Every unit carries a visible "Ad" label (or "Sponsored" for affiliate slots),
 * collapses per its zone definition on small screens, and never shifts layout
 * once loaded — the placeholder reserves the height up front.
 */
import { useEffect, useRef, useState } from 'react';
import { adZone } from '@fairwaylive/shared';
import { useAds, useRotatingCreative } from '../lib/adClient.tsx';

interface AdZoneProps {
  zoneKey: string;
  className?: string;
  /** Show nothing at all (not even a frame) when the zone has no inventory. */
  hideWhenEmpty?: boolean;
}

export function AdZone({ zoneKey, className = '', hideWhenEmpty = true }: AdZoneProps) {
  const { creative, slot, ref } = useRotatingCreative(zoneKey);
  const { clickThrough, track } = useAds();
  const zone = adZone(zoneKey);
  if (!zone) return null;

  // Zones marked `collapse` are desktop-only inventory.
  const responsive =
    zone.mobile === 'collapse' ? 'hidden lg:block' : zone.mobile === 'shrink' ? 'block' : 'block';

  if (!creative) {
    if (hideWhenEmpty) return null;
    return (
      <div className={`${responsive} ${className}`}>
        <ZoneFrame zone={zone.label} available>
          <div className="flex h-full items-center justify-center px-4 text-center">
            <p className="text-xs text-[var(--color-muted-dim)]">
              {slot?.suppressedReason ?? `${zone.label} · ${zone.size.w}×${zone.size.h} available`}
            </p>
          </div>
        </ZoneFrame>
      </div>
    );
  }

  const aspect = zone.size.w / zone.size.h;

  return (
    <div className={`${responsive} ${className}`}>
      <ZoneFrame zone={creative.isSample ? 'Ad · sample' : 'Ad'} sponsor={creative.advertiserName}>
        <a
          ref={ref as (node: HTMLAnchorElement | null) => void}
          href={clickThrough(creative)}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={() => track(creative, 'click')}
          className="block overflow-hidden rounded-[10px]"
          style={{ aspectRatio: String(aspect) }}
          aria-label={`Advertisement: ${creative.advertiserName}`}
        >
          {creative.kind === 'image' && creative.assetUrl ? (
            <img src={creative.assetUrl} alt={creative.altText} className="h-full w-full object-cover" loading="lazy" />
          ) : creative.kind === 'html' && creative.html ? (
            // Sanitised server-side before it ever reaches the client.
            <div className="h-full w-full" dangerouslySetInnerHTML={{ __html: creative.html }} />
          ) : creative.assetUrl ? (
            <video src={creative.assetUrl} muted autoPlay loop playsInline className="h-full w-full object-cover" />
          ) : null}
        </a>
      </ZoneFrame>
    </div>
  );
}

function ZoneFrame({
  zone,
  sponsor,
  available = false,
  children,
}: {
  zone: string;
  sponsor?: string;
  available?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`relative rounded-[12px] border p-1.5 ${
        available
          ? 'border-dashed border-[color-mix(in_oklab,var(--color-fairway-400)_30%,transparent)] bg-[color-mix(in_oklab,var(--color-fairway-800)_60%,transparent)]'
          : 'border-[color-mix(in_oklab,var(--color-fairway-400)_20%,transparent)] bg-[var(--color-fairway-850)]'
      }`}
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="rounded bg-[color-mix(in_oklab,var(--color-fairway-400)_22%,transparent)] px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted)]">
          {zone}
        </span>
        {sponsor ? <span className="truncate pl-2 text-[10px] text-[var(--color-muted-dim)]">{sponsor}</span> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * Full-screen interstitial. Configurable duration, a countdown, and a skip
 * button that appears after the sponsor's minimum exposure — which is the deal:
 * the sponsor gets guaranteed seconds, the viewer always gets an exit.
 */
export function InterstitialAd() {
  const { interstitial, dismissInterstitial, clickThrough, track } = useAds();
  const [elapsed, setElapsed] = useState(0);
  const shownAt = useRef<number>(Date.now());
  const impressionSent = useRef(false);

  useEffect(() => {
    if (!interstitial) return;
    shownAt.current = Date.now();
    setElapsed(0);
    impressionSent.current = false;
    const timer = window.setInterval(() => setElapsed((e) => e + 0.25), 250);
    return () => window.clearInterval(timer);
  }, [interstitial]);

  useEffect(() => {
    if (!interstitial || impressionSent.current) return;
    impressionSent.current = true;
    track(interstitial, 'impression');
    track(interstitial, 'viewable');
  }, [interstitial, track]);

  // Escape closes it once skipping is allowed, like any other modal.
  useEffect(() => {
    if (!interstitial) return;
    const skipAfter = interstitial.skipAfterSeconds ?? 5;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && elapsed >= skipAfter) {
        dismissInterstitial('skip', (Date.now() - shownAt.current) / 1000);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismissInterstitial, elapsed, interstitial]);

  // Auto-close when the booked duration is up.
  useEffect(() => {
    if (!interstitial) return;
    const duration = interstitial.durationSeconds ?? 15;
    if (elapsed >= duration) dismissInterstitial('complete', (Date.now() - shownAt.current) / 1000);
  }, [dismissInterstitial, elapsed, interstitial]);

  if (!interstitial) return null;

  const duration = interstitial.durationSeconds ?? 15;
  const skipAfter = interstitial.skipAfterSeconds ?? 5;
  const canSkip = elapsed >= skipAfter;
  const remaining = Math.max(0, Math.ceil(duration - elapsed));
  const untilSkip = Math.max(0, Math.ceil(skipAfter - elapsed));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(2,8,5,0.92)] p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Advertisement"
    >
      <div className="w-full max-w-[820px]">
        <div className="mb-2 flex items-center justify-between">
          <span className="rounded bg-[var(--color-gold-500)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#1a1204]">
            Ad{interstitial.isSample ? ' · sample' : ''}
          </span>
          <div className="flex items-center gap-3">
            <span className="tabular text-xs text-[var(--color-muted)]">closes in {remaining}s</span>
            <button
              type="button"
              disabled={!canSkip}
              onClick={() => dismissInterstitial('skip', (Date.now() - shownAt.current) / 1000)}
              className={`btn ${canSkip ? 'btn-ghost' : 'btn-ghost opacity-45'} !px-3 !py-1.5 !text-xs`}
            >
              {canSkip ? 'Skip ad ✕' : `Skip in ${untilSkip}`}
            </button>
          </div>
        </div>

        <a
          href={clickThrough(interstitial)}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={() => track(interstitial, 'click')}
          className="block overflow-hidden rounded-[var(--radius-card)] border border-[color-mix(in_oklab,var(--color-fairway-400)_28%,transparent)] shadow-[var(--shadow-board)]"
        >
          {interstitial.assetUrl ? (
            <img src={interstitial.assetUrl} alt={interstitial.altText} className="w-full" />
          ) : interstitial.html ? (
            <div dangerouslySetInnerHTML={{ __html: interstitial.html }} />
          ) : null}
        </a>

        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-[var(--color-fairway-700)]">
          <div
            className="h-full bg-[var(--color-fairway-300)] transition-[width] duration-250 ease-linear"
            style={{ width: `${Math.min(100, (elapsed / duration) * 100)}%` }}
          />
        </div>
        <p className="mt-2 text-center text-[11px] text-[var(--color-muted-dim)]">
          {interstitial.advertiserName} · Ads keep live scoring free for clubs on the Fairway Free plan.
        </p>
      </div>
    </div>
  );
}

/**
 * Big-screen ad rotator: full-bleed creative between leaderboard pages, plus a
 * persistent sponsor ticker. Clubhouse screens are the best inventory we sell,
 * so this is a first-class surface rather than a shrunken web banner.
 */
export function BigScreenAd({ zoneKey = 'bigscreen_rotator' }: { zoneKey?: string }) {
  const { creative, ref } = useRotatingCreative(zoneKey);
  if (!creative) return null;
  return (
    <div ref={ref as (node: HTMLDivElement | null) => void} className="relative h-full w-full">
      {creative.assetUrl ? (
        <img src={creative.assetUrl} alt={creative.altText} className="h-full w-full object-cover" />
      ) : null}
      <span className="absolute left-6 top-6 rounded bg-[rgba(2,8,5,0.7)] px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[var(--color-sand)]">
        Ad
      </span>
    </div>
  );
}

export function SponsorTicker({ zoneKey = 'bigscreen_ticker' }: { zoneKey?: string }) {
  const { slots } = useAds();
  const slot = slots[zoneKey];
  const names = slot?.creatives.map((c) => c.advertiserName) ?? [];
  if (!names.length) return null;
  const items = [...names, ...names, ...names, ...names];
  return (
    <div className="overflow-hidden border-t border-[color-mix(in_oklab,var(--color-fairway-400)_24%,transparent)] bg-[var(--color-fairway-950)] py-3">
      <div className="ticker-track whitespace-nowrap">
        {items.map((name, i) => (
          <span key={`${name}-${i}`} className="mx-8 text-lg font-semibold uppercase tracking-[0.18em] text-[var(--color-sand)]">
            {name}
            <span className="mx-8 text-[var(--color-fairway-400)]">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
}
